/**
 * Simple SVG-based Sankey diagram component
 * Replaces recharts Sankey to reduce bundle size
 * Handles simple 2-4 node layouts manually without layout library
 */

import React, { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { JobApplicationFlowStage, SankeyData } from '../types/visualization';
import {
  getSankeyLevelPaddingRatio,
  getSmallDataSankeyMargins,
} from '../utils/visualization/small-data-sankey-layout';
import { fitSankeyColumnNodeHeights } from '../utils/visualization/fit-sankey-column-node-heights';
import { useThrottledResize } from '../hooks/useThrottledResize';
import {
  JobFlowSankeyTooltipContent,
  type JobFlowSankeyTooltipPayload,
} from './JobFlowSankeyTooltipContent';

export interface SimpleSankeyLayoutEstimate {
  width: number;
  height: number;
}

interface SimpleSankeyProps {
  data: SankeyData;
  nodeWidth?: number;
  nodePadding?: number;
  smallData?: boolean;
  /** Avoids SSR/wrong first paint when the chart sits in a fixed-size card (e.g. SRP strip). */
  layoutEstimate?: SimpleSankeyLayoutEstimate;
}

/**
 * Sankey node / link colours — `jc-public-theme.css` tokens so light/dark stay aligned with the marketplace.
 */
const STAGE_COLORS: Record<JobApplicationFlowStage, string> = {
  browsed: 'var(--jc-filter-location-border)',
  viewed: 'var(--jc-accent)',
  saved: 'var(--jc-gold)',
  applied: 'var(--jc-cta)',
  interviewing: 'var(--jc-accent-soft)',
  offer: 'var(--jc-gold-line)',
  rejected: 'var(--jc-filter-company-border)',
  hired: 'var(--jc-filter-job-text)',
};

function getNodeColor(node: { name: string; jobFlowStage?: JobApplicationFlowStage }): string {
  if (node.jobFlowStage) return STAGE_COLORS[node.jobFlowStage];
  const name = node.name.toLowerCase();
  if (name.includes('browsed')) return STAGE_COLORS.browsed;
  if (name.includes('viewed')) return STAGE_COLORS.viewed;
  if (name.includes('saved')) return STAGE_COLORS.saved;
  if (name.includes('applied')) return STAGE_COLORS.applied;
  if (name.includes('interviewing')) return STAGE_COLORS.interviewing;
  if (name.includes('offer')) return STAGE_COLORS.offer;
  if (name.includes('rejected')) return STAGE_COLORS.rejected;
  if (name.includes('hired')) return STAGE_COLORS.hired;
  return 'var(--jc-muted)';
}

function initialDimensions(estimate?: SimpleSankeyLayoutEstimate): { width: number; height: number } {
  if (estimate && estimate.width >= 2 && estimate.height >= 2) {
    return { width: estimate.width, height: estimate.height };
  }
  return { width: 0, height: 0 };
}

/** Until the container is measured, `clientWidth`/`Height` are often 0 — still draw using fallbacks. */
const STRIP_LAYOUT_FALLBACK = { width: 360, height: 200 };
const TOOLS_LAYOUT_FALLBACK = { width: 400, height: 300 };

function resolveLayoutSize(
  measured: { width: number; height: number },
  estimate: SimpleSankeyLayoutEstimate | undefined,
  smallData: boolean,
): { width: number; height: number } {
  if (measured.width >= 2 && measured.height >= 2) {
    return { width: measured.width, height: measured.height };
  }
  if (estimate && estimate.width >= 2 && estimate.height >= 2) {
    return { width: estimate.width, height: estimate.height };
  }
  return smallData ? STRIP_LAYOUT_FALLBACK : TOOLS_LAYOUT_FALLBACK;
}

export function SimpleSankey({
  data,
  nodeWidth = 16,
  nodePadding = 72,
  smallData = false,
  layoutEstimate,
}: SimpleSankeyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState(() => initialDimensions(layoutEstimate));

  // Make responsive - use throttled resize to prevent lag
  const updateDimensions = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    let w = el.clientWidth;
    let h = el.clientHeight;
    if (w < 2 || h < 2) {
      const r = el.getBoundingClientRect();
      w = Math.max(Math.round(r.width), 0);
      h = Math.max(Math.round(r.height), 0);
    }
    if (w >= 2 && h >= 2) {
      setDimensions({ width: w, height: h });
    }
  }, []);

  useLayoutEffect(() => {
    updateDimensions();
  }, [updateDimensions]);

  useEffect(() => {
    updateDimensions();
  }, [updateDimensions]);

  // Use throttled resize to prevent lag
  useThrottledResize(updateDimensions);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);

  const [tip, setTip] = useState<{
    key: string;
    payload: JobFlowSankeyTooltipPayload;
    x: number;
    y: number;
    pinned: boolean;
  } | null>(null);

  const [tipPos, setTipPos] = useState({ left: 0, top: 0 });

  const cancelHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleUnpinnedHide = useCallback(
    (key: string) => {
      cancelHideTimer();
      hideTimerRef.current = window.setTimeout(() => {
        setTip((cur) => (cur && !cur.pinned && cur.key === key ? null : cur));
        hideTimerRef.current = null;
      }, 200);
    },
    [cancelHideTimer]
  );

  useLayoutEffect(() => {
    if (!tip) return;
    const run = () => {
      const el = tooltipRef.current;
      if (!el) return;
      const pad = 10;
      let l = tip.x + 14;
      let t = tip.y + 14;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w > 0 && h > 0) {
        l = Math.min(l, window.innerWidth - w - pad);
        t = Math.min(t, window.innerHeight - h - pad);
        l = Math.max(pad, l);
        t = Math.max(pad, t);
      }
      setTipPos({ left: l, top: t });
    };
    run();
    let innerRaf: number | null = null;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(run);
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf != null) cancelAnimationFrame(innerRaf);
    };
  }, [tip]);

  useEffect(() => {
    if (!tip?.pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTip(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tip?.pinned]);

  useEffect(() => {
    if (!tip?.pinned) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (tooltipRef.current?.contains(target)) return;
      if ((e.target as HTMLElement).closest('[data-sankey-hit]')) return;
      setTip(null);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [tip?.pinned]);

  const nodes = data.nodes;
  const links = data.links;

  if (nodes.length === 0) return null;

  const { width: layoutW, height: layoutH } = resolveLayoutSize(dimensions, layoutEstimate, smallData);

  // Compress margins and spacing on smaller screens
  const isSmallScreen = layoutW < 768;
  const margin = smallData
    ? getSmallDataSankeyMargins(layoutW)
    : isSmallScreen
      ? { top: 20, right: 24, bottom: 20, left: 24 }
      : { top: 32, right: 48, bottom: 32, left: 48 };
  const chartWidth = Math.max(1, layoutW - margin.left - margin.right);
  const chartHeight = Math.max(1, layoutH - margin.top - margin.bottom);

  // Compress node width and padding on smaller screens
  const effectiveNodeWidth = isSmallScreen ? Math.max(8, nodeWidth * 0.6) : nodeWidth;
  const effectiveNodePadding = isSmallScreen ? Math.max(24, nodePadding * 0.4) : nodePadding;

  // Calculate flow for each node
  const inLinksByNode = new Map<number, number[]>();
  const outLinksByNode = new Map<number, number[]>();

  links.forEach((link, i) => {
    if (!inLinksByNode.has(link.target)) {
      inLinksByNode.set(link.target, []);
    }
    inLinksByNode.get(link.target)!.push(i);

    if (!outLinksByNode.has(link.source)) {
      outLinksByNode.set(link.source, []);
    }
    outLinksByNode.get(link.source)!.push(i);
  });

  // Calculate node sizes based on actual flow values (proportional sizing)
  // Use flow from node data if available, otherwise calculate from links
  // IMPORTANT: Use original values (not scaled) for display, but scaled values for sizing
  const nodeFlows: number[] = [];
  nodes.forEach((node, i) => {
    if (node.flow !== undefined) {
      // Use explicit flow value from node (actual job count)
      nodeFlows[i] = node.flow;
    } else {
      // Fallback: calculate from links - use originalValue if available for display
      const inFlow = (inLinksByNode.get(i) || []).reduce((sum, linkIdx) => {
        const link = links[linkIdx];
        return sum + (link.originalValue ?? link.value);
      }, 0);
      const outFlow = (outLinksByNode.get(i) || []).reduce((sum, linkIdx) => {
        const link = links[linkIdx];
        return sum + (link.originalValue ?? link.value);
      }, 0);
      nodeFlows[i] = Math.max(inFlow, outFlow);
    }
  });

  // Find max flow for proportional scaling
  const maxFlow = Math.max(...nodeFlows, 1);
  const minBarSize = isSmallScreen ? 30 : 40;
  const maxBarSize = smallData ? chartHeight * 0.58 : chartHeight * 0.7;
  
  // Calculate proportional node sizes - truly proportional
  const nodeSizes: number[] = [];
  nodes.forEach((_, i) => {
    if (nodeFlows[i] === 0) {
      nodeSizes[i] = minBarSize;
    } else {
      // Proportional: if maxFlow is 20 and current is 2, size should be 2/20 of maxBarSize
      const proportion = nodeFlows[i] / maxFlow;
      nodeSizes[i] = Math.max(minBarSize, maxBarSize * proportion);
    }
  });

  // Calculate node levels so each node is to the right of all its predecessors.
  // Level = 1 + max(predecessor levels), so e.g. Applied (Browsed+Viewed+Saved) is right of Viewed/Saved.
  const nodeLevels: number[] = new Array(nodes.length).fill(-1);
  const nodesByLevel = new Map<number, number[]>();

  // Roots (no incoming links) get level 0
  nodes.forEach((_, i) => {
    const inLinks = inLinksByNode.get(i) || [];
    if (inLinks.length === 0) {
      nodeLevels[i] = 0;
    }
  });

  // Assign levels in dependency order: level[i] = 1 + max(level[source] for source of incoming links)
  let changed = true;
  while (changed) {
    changed = false;
    nodes.forEach((_, i) => {
      if (nodeLevels[i] >= 0) return;
      const inLinkIndices = inLinksByNode.get(i) || [];
      const predLevels = inLinkIndices.map((linkIdx) => nodeLevels[links[linkIdx].source]);
      if (predLevels.length === 0 || predLevels.some((l) => l < 0)) return;
      const level = 1 + Math.max(...predLevels);
      nodeLevels[i] = level;
      changed = true;
    });
  }

  // Fallback: any node still unassigned (e.g. disconnected) gets level 0
  nodes.forEach((_, i) => {
    if (nodeLevels[i] < 0) nodeLevels[i] = 0;
  });

  // Group nodes by level for layout
  nodes.forEach((_, i) => {
    const level = nodeLevels[i];
    if (!nodesByLevel.has(level)) {
      nodesByLevel.set(level, []);
    }
    nodesByLevel.get(level)!.push(i);
  });

  fitSankeyColumnNodeHeights({
    nodesByLevel,
    nodeSizes,
    chartHeight,
    nodePadding: effectiveNodePadding,
  });

  // Always use horizontal flow (left to right), nodes grouped by level in columns
  const nodeXs: number[] = [];
  const nodeYs: number[] = [];

  const maxLevel = Math.max(...nodeLevels);
  const levelWidth = maxLevel > 0 ? chartWidth / (maxLevel + 1) : chartWidth;
  const levelPaddingRatio = getSankeyLevelPaddingRatio({
    smallData,
    containerWidth: layoutW,
    isSmallScreen,
  });
  const levelPadding = maxLevel > 0 ? levelWidth * levelPaddingRatio : 0;
  const actualLevelWidth = maxLevel > 0 ? (chartWidth - levelPadding * maxLevel) / (maxLevel + 1) : chartWidth;

  nodesByLevel.forEach((nodeIndices, level) => {
    const levelX = level * (actualLevelWidth + levelPadding) + actualLevelWidth / 2;
    
    // Calculate total height needed for this level (sum of all node heights + padding)
    const totalHeightForLevel = nodeIndices.reduce((sum, nodeIdx) => sum + nodeSizes[nodeIdx], 0) + 
                                 effectiveNodePadding * (nodeIndices.length - 1);
    let currentY = (chartHeight - totalHeightForLevel) / 2;

    nodeIndices.forEach(nodeIdx => {
      nodeXs[nodeIdx] = levelX;
      nodeYs[nodeIdx] = currentY + nodeSizes[nodeIdx] / 2;
      currentY += nodeSizes[nodeIdx] + effectiveNodePadding;
    });
  });

  // Calculate link paths - always horizontal flow
  const linkPaths = links.map((link) => {
    const sourceX = nodeXs[link.source];
    const targetX = nodeXs[link.target];
    const sourceY = nodeYs[link.source];
    const targetY = nodeYs[link.target];

    // Calculate exit/entry points on the right/left edges of nodes
    const sourceExitX = sourceX + effectiveNodeWidth / 2; // Exit from right side of vertical bar
    const targetEntryX = targetX - effectiveNodeWidth / 2; // Enter from left side of vertical bar

    // Control points for smooth horizontal curves
    const dx = targetEntryX - sourceExitX;
    const sourceControlX = sourceExitX + dx * 0.5;
    const targetControlX = targetEntryX - dx * 0.5;

    // Link width based on scaled value for visualization
    const linkWidth = link.value > 0 ? Math.max(2, link.value * 2) : 1;
    // Use original value for display (if available), otherwise use scaled value
    const displayValue = link.originalValue ?? link.value;

    return {
      path: `M${sourceExitX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetEntryX},${targetY}`,
      width: linkWidth,
      value: displayValue, // Use original value for display
      midX: (sourceExitX + targetEntryX) / 2,
      midY: (sourceY + targetY) / 2,
    };
  });

  return (
    <div
      ref={containerRef}
      className={
        smallData
          ? 'relative h-full min-h-[240px] w-full min-w-0'
          : 'relative h-full min-h-0 w-full min-w-0'
      }
    >
      <svg
        width="100%"
        height="100%"
        className="block h-full w-full"
        viewBox={`0 0 ${layoutW} ${layoutH}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Render links first (behind nodes) */}
          {linkPaths.map((linkPath, i) => {
            const link = links[i];
            const sourceNode = nodes[link.source];
            const targetNode = nodes[link.target];
            const linkColor = getNodeColor(sourceNode);
            const linkKey = `l-${i}`;
            const linkMeta = link.tooltipMeta;
            const hitW = Math.max(32, linkPath.width + 24);
            return (
              <g key={i}>
                <path
                  d={linkPath.path}
                  fill="none"
                  stroke={linkColor}
                  strokeWidth={linkPath.width}
                  strokeOpacity={linkPath.value > 0 ? 0.5 : 0.2}
                  style={{ pointerEvents: 'none' }}
                />
                {linkMeta ? (
                  <path
                    d={linkPath.path}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={hitW}
                    strokeLinecap="round"
                    data-sankey-hit="true"
                    className="cursor-pointer touch-manipulation outline-none"
                    tabIndex={0}
                    aria-label={`${sourceNode.name} to ${targetNode.name}`}
                    onPointerEnter={(e) => {
                      cancelHideTimer();
                      setTip({
                        key: linkKey,
                        payload: {
                          kind: 'link',
                          fromName: sourceNode.name,
                          toName: targetNode.name,
                          meta: linkMeta,
                          accentColor: linkColor,
                        },
                        x: e.clientX,
                        y: e.clientY,
                        pinned: false,
                      });
                    }}
                    onPointerMove={(e) => {
                      setTip((cur) =>
                        cur && !cur.pinned && cur.key === linkKey
                          ? { ...cur, x: e.clientX, y: e.clientY }
                          : cur
                      );
                    }}
                    onPointerLeave={() => scheduleUnpinnedHide(linkKey)}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      cancelHideTimer();
                      setTip({
                        key: linkKey,
                        payload: {
                          kind: 'link',
                          fromName: sourceNode.name,
                          toName: targetNode.name,
                          meta: linkMeta,
                          accentColor: linkColor,
                        },
                        x: e.clientX,
                        y: e.clientY,
                        pinned: true,
                      });
                    }}
                  />
                ) : null}
              {/* Link value label - hide for small data */}
              {!smallData && (
                <text
                  x={linkPath.midX}
                  y={linkPath.midY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--jc-ink)"
                  fontSize={12}
                  fontWeight="bold"
                  style={{ pointerEvents: 'none' }}
                >
                  {Math.round(link.originalValue ?? link.value)} jobs
                </text>
              )}
              </g>
            );
          })}

          {/* Render nodes - always vertical bars (height varies by flow, width is fixed) */}
          {nodes.map((node, i) => {
            const nodeX = nodeXs[i];
            const nodeY = nodeYs[i];
            const nodeSize = nodeSizes[i];

            const nodeColor = getNodeColor(node);
            const textOffset = isSmallScreen ? 18 : 25; // Offset text from bars
            const fontSize = isSmallScreen ? (smallData ? 10 : 11) : (smallData ? 12 : 14);
            const countFontSize = isSmallScreen ? (smallData ? 9 : 10) : (smallData ? 10 : 11);

            const nodeKey = `n-${i}`;
            const nodeMeta = node.tooltipMeta;

            return (
              <g key={i}>
                <rect
                  x={nodeX - effectiveNodeWidth / 2}
                  y={nodeY - nodeSize / 2}
                  width={effectiveNodeWidth}
                  height={nodeSize}
                  fill={nodeColor}
                  fillOpacity={0.8}
                  rx={4}
                  ry={4}
                  className={
                    nodeMeta
                      ? 'cursor-pointer touch-manipulation outline-none focus-visible:ring-2 focus-visible:ring-[#98CFA8] focus-visible:ring-offset-1'
                      : undefined
                  }
                  data-sankey-hit={nodeMeta ? 'true' : undefined}
                  tabIndex={nodeMeta ? 0 : undefined}
                  aria-label={nodeMeta ? `${node.name}, ${nodeFlows[i]} jobs` : undefined}
                  onPointerEnter={
                    nodeMeta
                      ? (e) => {
                          cancelHideTimer();
                          setTip({
                            key: nodeKey,
                            payload: {
                              kind: 'node',
                              displayName: node.name,
                              meta: nodeMeta,
                              accentColor: nodeColor,
                            },
                            x: e.clientX,
                            y: e.clientY,
                            pinned: false,
                          });
                        }
                      : undefined
                  }
                  onPointerMove={
                    nodeMeta
                      ? (e) => {
                          setTip((cur) =>
                            cur && !cur.pinned && cur.key === nodeKey
                              ? { ...cur, x: e.clientX, y: e.clientY }
                              : cur
                          );
                        }
                      : undefined
                  }
                  onPointerLeave={
                    nodeMeta ? () => scheduleUnpinnedHide(nodeKey) : undefined
                  }
                  onPointerDown={
                    nodeMeta
                      ? (e) => {
                          e.stopPropagation();
                          cancelHideTimer();
                          setTip({
                            key: nodeKey,
                            payload: {
                              kind: 'node',
                              displayName: node.name,
                              meta: nodeMeta,
                              accentColor: nodeColor,
                            },
                            x: e.clientX,
                            y: e.clientY,
                            pinned: true,
                          });
                        }
                      : undefined
                  }
                />
                <text
                  x={nodeX + textOffset}
                  y={nodeY}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fill="var(--jc-ink)"
                  fontSize={fontSize}
                  fontWeight="bold"
                  style={{ pointerEvents: 'none' }}
                >
                  {node.name}
                </text>
                {/* Show count next to label */}
                {nodeFlows[i] > 0 && (
                  <text
                    x={nodeX + textOffset}
                    y={nodeY + (isSmallScreen ? 12 : 16)}
                    textAnchor="start"
                    dominantBaseline="middle"
                    fill="var(--jc-muted)"
                    fontSize={countFontSize}
                    style={{ pointerEvents: 'none' }}
                  >
                    {nodeFlows[i]} jobs
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      {tip && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={tooltipRef}
              data-sankey-tooltip
              className="fixed z-[200] max-w-[min(calc(100vw-16px),20rem)] pointer-events-none rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-gradient-to-br from-white via-[#FAF7F2] to-[#F2EADF] dark:from-[#1F1509] dark:via-[#1C160F] dark:to-[#170F07] p-4 shadow-[0_16px_48px_-12px_rgba(30,26,20,0.28)] dark:shadow-[0_20px_50px_-8px_rgba(0,0,0,0.65)] transition-[opacity,transform] duration-150 ease-out"
              style={{
                left: tipPos.left,
                top: tipPos.top,
                opacity: tip ? 1 : 0,
                transform: 'translateZ(0)',
              }}
              role="tooltip"
            >
              <JobFlowSankeyTooltipContent payload={tip.payload} />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
